/**
 * P-8 日報設定 — 可視性クラス (`/settings/daily-report/visibility-classes`、§14-5-5)。
 * 項目レベル可視性クラスの追加・編集・削除と、3軸（記入者/店舗管理者/その他の閲覧者）の
 * 表示段階をまとめて設定する。
 * バックエンド: apps/api/src/routes/daily-reports.ts GET/PUT/DELETE /daily-reports/config/visibility-classes、
 *              GET/PUT /daily-reports/config/visibility-rules。
 * 設計: docs/design/v05-daily-report-detail.md §14-5-5
 *
 * UIピア: RequestFieldVisibilitySettingsPage.tsx (TD-28 D-24〜D-28)。ただし日報のルールは
 * `default_display_level`/`approval_level`/`condition_set_id` を持たない固定3軸のみのため、
 * peer のように「ルールを後から個別追加、未設定の軸は既定へフォールバック」という設計にせず、
 * クラス1件＝3軸すべてを毎回1フォームでまとめて保存する（設定し忘れた軸が黙って fail-closed に
 * なることを構造的に防ぐ、§14-5-5）。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  fetchDailyReportVisibilityClasses, saveDailyReportVisibilityClass, deleteDailyReportVisibilityClass,
  fetchDailyReportVisibilityRules, saveDailyReportVisibilityRules,
  DAILY_REPORT_VISIBILITY_CONTEXTS,
  type DailyReportVisibilityClass, type DailyReportVisibilityContext, type DailyReportDisplayLevel,
} from '@/lib/daily-report-api';
import { DAILY_REPORT_DISPLAY_LEVEL_LABEL, DAILY_REPORT_VISIBILITY_CONTEXT_LABEL } from '@/lib/daily-report-visibility-labels';

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const errorMessage = (e: unknown, fallback: string) => (e instanceof ApiClientError ? e.message : fallback);

type RulesByContext = Record<DailyReportVisibilityContext, DailyReportDisplayLevel>;
const emptyRules = (): RulesByContext => ({ author: 'none', node_manager: 'none', viewer: 'none' });

export function DailyReportVisibilityClassesSettingsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ cls: DailyReportVisibilityClass | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DailyReportVisibilityClass | null>(null);

  const { data: classes = [], isLoading, isError } = useQuery({
    queryKey: ['daily-report-visibility-classes'], queryFn: fetchDailyReportVisibilityClasses,
  });
  const { data: rules = [], isError: rulesError } = useQuery({
    queryKey: ['daily-report-visibility-rules'], queryFn: fetchDailyReportVisibilityRules,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['daily-report-visibility-classes'] });
    void qc.invalidateQueries({ queryKey: ['daily-report-visibility-rules'] });
  };

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDailyReportVisibilityClass(id),
    onSuccess: () => { toast.success('削除しました'); setDeleteTarget(null); refresh(); },
    onError: (e) => { toast.error(errorMessage(e, '削除に失敗しました（項目から参照されている可能性があります）')); setDeleteTarget(null); },
  });

  const sortedClasses = useMemo(() => [...classes].sort((a, b) => a.sort_order - b.sort_order), [classes]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">可視性クラス</h2>
          <p className="text-sm text-text-sub mt-1 max-w-2xl">
            項目に割り当てる「誰にどこまで見せるか」のクラスを管理します。クラスを割り当てていない項目は、
            誰にも表示されません（項目定義タブの「可視性クラス」欄で割り当ててください）。
          </p>
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft({ cls: null })}>
          クラスを追加
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-text-sub">読み込み中…</p>
      ) : isError ? (
        <p className="text-sm text-danger-text" role="alert">クラス一覧の取得に失敗しました。</p>
      ) : sortedClasses.length === 0 ? (
        <EmptyState message="可視性クラスがまだありません" description="「クラスを追加」から登録してください。登録するまで、項目に可視性クラスを割り当てても表示されません。" />
      ) : (
        <ul className="space-y-2">
          {sortedClasses.map((cls) => {
            const clsRules = rules.filter((r) => r.class_id === cls.class_id);
            return (
              <li key={cls.class_id} className="border border-surface-border rounded-lg bg-white p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-text-body break-words">
                      {cls.label}
                      {cls.status === 'archived' && (
                        <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border align-middle">無効化済み</span>
                      )}
                    </p>
                    <p className="text-xs text-text-sub mt-0.5">
                      コード: {cls.code}{cls.description ? ` ／ ${cls.description}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="secondary" size="sm" leftIcon={<Pencil size={14} />} onClick={() => setDraft({ cls })}>編集</Button>
                    <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} />} onClick={() => setDeleteTarget(cls)}>削除</Button>
                  </div>
                </div>
                {rulesError ? (
                  <p className="text-xs text-danger-text mt-2" role="alert">表示ルールの取得に失敗しました。実際に適用されている設定は不明です（再読み込みしてください）。</p>
                ) : (
                  <ul className="mt-2 pt-2 border-t border-surface-border grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                    {DAILY_REPORT_VISIBILITY_CONTEXTS.map((ctx) => {
                      const level = clsRules.find((r) => r.context === ctx)?.display_level ?? 'none';
                      return (
                        <li key={ctx} className="text-xs text-text-sub">
                          {DAILY_REPORT_VISIBILITY_CONTEXT_LABEL[ctx]}: <span className="font-medium text-text-body">{DAILY_REPORT_DISPLAY_LEVEL_LABEL[level]}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {draft && (
        <ClassModal
          cls={draft.cls}
          initialRules={draft.cls ? rules.filter((r) => r.class_id === draft.cls!.class_id) : []}
          onClose={() => setDraft(null)}
          onDone={() => { setDraft(null); refresh(); }}
          onPartialProgress={refresh}
        />
      )}
      <ConfirmDialog open={!!deleteTarget} title="削除しますか？"
        message="この可視性クラスを削除します。項目に割り当て済みの場合は削除できません。"
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.class_id)}
        onClose={() => setDeleteTarget(null)} />
    </div>
  );
}

function ClassModal({ cls, initialRules, onClose, onDone, onPartialProgress }: {
  cls: DailyReportVisibilityClass | null;
  initialRules: { context: DailyReportVisibilityContext; display_level: DailyReportDisplayLevel }[];
  onClose: () => void;
  onDone: () => void;
  /** ⚠️ QA/design-and-codeレビュー指摘 (MEDIUM是正): クラスPUT成功→ルールPUT失敗の非原子的な
   *  保存で、①一覧に反映されず孤立クラスの存在に気づけない ②再試行が新規作成扱いになり
   *  code の UNIQUE 制約で紛らわしい409になる、の2つを防ぐ。クラス保存が成功した時点で
   *  一覧をrefreshし、以後の再試行は作成済みclass_idへのPUT (更新) として扱う。 */
  onPartialProgress: () => void;
}) {
  const isEdit = !!cls;
  const [code, setCode] = useState(cls?.code ?? '');
  const [label, setLabel] = useState(cls?.label ?? '');
  const [description, setDescription] = useState(cls?.description ?? '');
  const [rulesState, setRulesState] = useState<RulesByContext>(() => {
    const base = emptyRules();
    for (const r of initialRules) base[r.context] = r.display_level;
    return base;
  });
  const [createdClassId, setCreatedClassId] = useState<string | null>(null);
  const effectiveClassId = cls?.class_id ?? createdClassId;

  const mutation = useMutation({
    mutationFn: async () => {
      const classRes = await saveDailyReportVisibilityClass({
        ...(effectiveClassId ? { class_id: effectiveClassId } : {}),
        code, label, description: description || null,
      });
      if (!effectiveClassId) setCreatedClassId(classRes.class_id);
      onPartialProgress();
      await saveDailyReportVisibilityRules({
        class_id: classRes.class_id,
        rules: DAILY_REPORT_VISIBILITY_CONTEXTS.map((context) => ({ context, display_level: rulesState[context] })),
      });
    },
    onSuccess: () => { toast.success(isEdit ? 'クラスを更新しました' : 'クラスを追加しました'); onDone(); },
    onError: (e) => toast.error(errorMessage(
      e, `${isEdit ? '更新' : '追加'}に失敗しました。表示ルールの保存でエラーが発生した場合、クラス自体は保存済みのことがあります（一覧を確認のうえ、もう一度保存してください）`,
    )),
  });

  const allNone = DAILY_REPORT_VISIBILITY_CONTEXTS.every((ctx) => rulesState[ctx] === 'none');

  return (
    <Modal open onClose={onClose} title={isEdit ? '可視性クラスを編集' : '可視性クラスを追加'}>
      <div className="space-y-4">
        <div>
          <label htmlFor="drvc-code" className="block text-sm text-text-sub mb-1">コード</label>
          <input id="drvc-code" className={inputCls} value={code} disabled={isEdit || !!createdClassId}
            onChange={(e) => setCode(e.target.value)} placeholder="public" maxLength={100} />
        </div>
        <div>
          <label htmlFor="drvc-label" className="block text-sm text-text-sub mb-1">名称</label>
          <input id="drvc-label" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="公開" maxLength={200} />
        </div>
        <div>
          <label htmlFor="drvc-desc" className="block text-sm text-text-sub mb-1">説明（任意）</label>
          <input id="drvc-desc" className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
        </div>
        <div className="space-y-2">
          <p className="block text-sm text-text-sub">表示段階（3つの場面すべてを設定してください）</p>
          {DAILY_REPORT_VISIBILITY_CONTEXTS.map((ctx) => (
            <div key={ctx}>
              <label htmlFor={`drvc-rule-${ctx}`} className="block text-xs text-text-sub mb-1">{DAILY_REPORT_VISIBILITY_CONTEXT_LABEL[ctx]}</label>
              <Select value={rulesState[ctx]} onValueChange={(v) => setRulesState({ ...rulesState, [ctx]: v as DailyReportDisplayLevel })}>
                <SelectTrigger id={`drvc-rule-${ctx}`} className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DAILY_REPORT_DISPLAY_LEVEL_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
          {allNone && (
            <p className="text-xs text-alert-text flex items-start gap-1">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" aria-hidden />
              すべて「表示しない」のままだと、このクラスを割り当てた項目は誰にも表示されません。
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!code.trim() || !label.trim()} loading={mutation.isPending}
            onClick={() => mutation.mutate()}>{isEdit ? '保存' : '追加'}</Button>
        </div>
      </div>
    </Modal>
  );
}
